import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class LazyVideo extends Component {
  connectedCallback() {
    super.connectedCallback();

    const template = this.querySelector('template');
    if (!template) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.appendChild(template.content.cloneNode(true));
            template.remove();

            const poster = this.querySelector('picture');
            const video = this.querySelector('video');

            if (video && video.hasAttribute('muted')) {
              video.muted = true;
              video.defaultMuted = true;
            }

            if (video && prefersReducedMotion()) {
              video.removeAttribute('autoplay');
              video.pause();
            } else if (video && video.hasAttribute('autoplay')) {
              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
              }
            }

            if (poster && video) {
              const removePoster = () => poster.remove();
              // An autoplaying video keeps its poster until the first frame is actually on screen.
              // A controls-only video never fires `playing` by itself, so drop the poster as soon as
              // a frame is decoded, otherwise the poster hides the video for good.
              video.addEventListener('playing', removePoster, { once: true });
              if (!video.hasAttribute('autoplay')) {
                video.addEventListener('loadeddata', removePoster, { once: true });
              }
            } else if (poster) {
              poster.remove();
            }

            observer.disconnect();
            if (video) this.#pauseOffscreen(video);
          }
        });
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#visibilityObserver?.disconnect();
  }

  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;

  /**
   * Pause a playing video while it is off screen and resume it on return, so a page with many
   * autoplaying videos only decodes the visible ones. Only a pause made here is undone: a video
   * the user paused (or never autoplayed) stays paused.
   * @param {HTMLVideoElement} video
   */
  #pauseOffscreen(video) {
    let pausedHere = false;

    this.#visibilityObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (!entry.isIntersecting && !video.paused) {
        video.pause();
        pausedHere = true;
      } else if (entry.isIntersecting && pausedHere) {
        pausedHere = false;
        video.play()?.catch(() => {});
      }
    });

    this.#visibilityObserver.observe(this);
  }
}

customElements.define('lazy-video', LazyVideo);
